"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import { Users, Target, Award, Heart, Lightbulb, Globe } from "lucide-react";

const ValueCard = ({ icon: Icon, title, description, color }) => {
  return (
    <div className="group relative bg-white/60 backdrop-blur-md border border-white/40 rounded-2xl p-6 hover:bg-white/80 transition-all duration-300 hover:scale-105 hover:shadow-xl">
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-xl ${color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#1E293B] mb-2">{title}</h3>
          {/* <p className="text-[#64748B] text-sm leading-relaxed">{description}</p> */}
        </div>
      </div>
    </div>
  );
};

function CareersDescription() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const values = [
    {
      icon: Users,
      title: "Collaborative Culture",
      description: "Work with talented professionals in a supportive environment that values teamwork and knowledge sharing.",
      color: "bg-gradient-to-br from-[#1719A8] to-[#3B82F6]"
    },
    {
      icon: Target,
      title: "Growth Opportunities",
      description: "Advance your career with continuous learning, skill development, and leadership opportunities.",
      color: "bg-gradient-to-br from-[#D96F2B] to-[#C9C51A]"
    },
    {
      icon: Award,
      title: "Excellence Recognition",
      description: "Your contributions are valued and recognized through competitive compensation and rewards programs.",
      color: "bg-gradient-to-br from-[#C9C51A] to-[#10B981]"
    },
    {
      icon: Heart,
      title: "Work-Life Balance",
      description: "Enjoy flexible working arrangements and comprehensive benefits that support your well-being.",
      color: "bg-gradient-to-br from-[#8B5CF6] to-[#1719A8]"
    },
    {
      icon: Lightbulb,
      title: "Innovation Focus",
      description: "Be part of cutting-edge projects using the latest technologies and industry best practices.",
      color: "bg-gradient-to-br from-[#EF4444] to-[#D96F2B]"
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Contribute to solutions that make a difference for clients and communities worldwide.",
      color: "bg-gradient-to-br from-[#10B981] to-[#1719A8]"
    }
  ];

  return (
    <section className="relative pb-10 pt-2 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40" aria-labelledby="careers-description-heading">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 lg:w-96 lg:h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 lg:w-96 lg:h-96 bg-indigo-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-orange-200/15 rounded-full blur-2xl"></div>
      </div>

      <div className="container max-w-7xl mx-auto px-4 lg:px-6 relative">
        {/* Section Header */}
        <header className="text-center mb-6">
          <div id="careers-description-heading" className="inline-flex items-center px-6 py-2 bg-[#1719A8]/90 backdrop-blur-md border border-[#1719A8]/60 rounded-full mb-6 shadow-lg">
            <span className="w-4 h-4 bg-white rounded-full mr-3 animate-pulse" aria-hidden="true"></span>
            <span className="text-2xl lg:text-3xl text-white font-semibold uppercase tracking-wider">
              Why Join Us
            </span>
          </div>
          {/* <h1 id="careers-description-heading" className="text-4xl lg:text-6xl font-bold text-slate-900 mb-3 leading-tight font-heading">
            Build Your Future
            <span className="block leading-normal bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              With Vasta Info
            </span>
          </h1> */}
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-body">
            Join a dynamic team of professionals dedicated to innovation, excellence, and making a meaningful impact
            in the technology industry. We offer exciting opportunities for growth and development.
          </p>
        </header>

        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Main Description */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-3xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E293B] mb-6 font-heading">Our Mission</h2>
              <p className="text-[#64748B] leading-relaxed mb-4 font-body">
                At Vasta Info, we're committed to delivering innovative technology solutions that drive business
                success for our clients. We believe in fostering a culture of continuous learning, collaboration,
                and professional growth.
              </p>
              <p className="text-[#64748B] leading-relaxed font-body">
                Our team consists of passionate professionals who are dedicated to excellence and committed to
                staying at the forefront of technology trends and industry best practices.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-3xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E293B] mb-6 font-heading">Career Development</h2>
              <p className="text-[#64748B] leading-relaxed mb-4 font-body">
                We invest in our people through comprehensive training programs, mentorship opportunities,
                and support for professional certifications. Your growth is our priority.
              </p>
              <p className="text-[#64748B] leading-relaxed font-body">
                From junior developers to senior architects, we provide clear career paths and the resources
                you need to achieve your professional goals.
              </p>
            </div>
          </div>

          {/* Values Grid */}
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-center text-[#1E293B] mb-4 font-heading">
              Our Values
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <ValueCard
                  key={index}
                  icon={value.icon}
                  title={value.title}
                  description={value.description}
                  color={value.color}
                />
              ))}
            </div>
          </div>

          {/* Call to Action Section */}
          {/* <div className="bg-gradient-to-br from-[#1719A8] to-[#0F1280] rounded-3xl p-8 lg:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-6 font-heading">Ready to Start Your Journey?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto font-body">
              Explore our current openings and find the perfect opportunity to advance your career
              with a company that values innovation, growth, and excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.getElementById('job-listings')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-3 bg-white text-[#1719A8] font-semibold rounded-full hover:bg-gray-100 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1719A8]"
                aria-label="View available job positions"
              >
                View Open Positions
              </button>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
}

export default CareersDescription;
